import { BoardID } from 'BoardID';
import { MaterialPatternString, SubBoardPatternString } from 'Pattern';
import { BoardType, Material } from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import Chevron from 'boardClasses/chevron/Chevron';
import Pinwheel from 'boardClasses/pinwheel/Pinwheel';
import Plaid from 'boardClasses/plaid/Plaid';
import QuarterSquare from 'boardClasses/quarterSquare/QuarterSquare';
import Strips from 'boardClasses/strips/Strips';
import StripsAdvanced from 'boardClasses/stripsAdvanced/StripsAdvanced';
import WeaveClass from 'boardClasses/weave/Weave';
import ZigZag from 'boardClasses/zigZag/ZigZag';

export const POPULAR_BOARDS_BY_NAME: { [name: string]: BoardBase } = {
  brick: new StripsAdvanced(
    '6d146305-02c4-0567-899e-ceb842c56f73' as BoardID,
    'Brick',
    {
      thickness: 1.5,
      pattern:
        '1A .25B 1A^ .25B 1A .25B 1A^ .25B 1A .25B 1A^ .25B 1A .25B 1A^ .25B 1A' as SubBoardPatternString,
      subpatterns: [
        '1.5W .25M 1.5W .25M 1.5W .25M 1.5W .25M 1.5W .25M 1.5W .25M 1.5W .25M 1.5W .25M 1.5W .25M .75W' as MaterialPatternString,
        '8.25M 8.25M' as MaterialPatternString,
      ],
    }
  ),
  bubble: new QuarterSquare(
    '68781c61-c7c5-fac7-8bd3-903a13708a44' as BoardID,
    'Bubble',
    {
      pattern: '.2 .28 .36 .5 .6 .76 .96 1.16 1.32' as MaterialPatternString,
      wood1: Material.M,
      wood2: Material.W,
      thickness: 1.5,
    }
  ),
  checkerboard: new Strips(
    '7bf4dff3-9b10-8c86-9e4c-bc2722f7487f' as BoardID,
    'Checkerboard',
    {
      numRows: 16,
      rowHeight: 1,
      thickness: 1.5,
      pattern: '1W 1M 1W 1M 1W 1M 1W 1M 1W 1M 1W 1M' as MaterialPatternString,
      flip: true,
    }
  ),
  chevron: new Chevron(
    'cbe561ca-c873-b57b-b366-cb7c6caef62c' as BoardID,
    'Chevron',
    {
      numRows: 16,
      rowHeight: 1,
      thickness: 1.5,
      rotate: 30,
      pattern:
        '1M 1W 1M 1W 1M 1W 1M 1W 1M 1W 1M 1W 1M' as MaterialPatternString,
    }
  ),
  dna: new StripsAdvanced(
    '39f12095-9e74-c57e-a90e-f6e14c75bf44' as BoardID,
    'DNA',
    {
      thickness: 1.5,
      pattern:
        '.5D .5C .5A .5C^ .5D^ .5D^ .5C^ .5B .5C .5D .5D .5C .5A .5C^ .5D^ .5D^ .5C^ .5B .5C .5D .5D .5C .5A .5C^ .5D^ .5D^ .5C^ .5B .5C .5D .5D .5C .5A .5C^ .5D^' as SubBoardPatternString,
      subpatterns: [
        '.5M .5M .5M .5M .5M .5C .5M .5M .5M .5M .5M .5M .5C .5M .5M .5M .5M .5M .5M .5C .5M .5M .5M .5M .5M' as MaterialPatternString,
        '.5M .5M .5M .5M .5M .5W .5M .5M .5M .5M .5M .5M .5W .5M .5M .5M .5M .5M .5M .5W .5M .5M .5M .5M .5M' as MaterialPatternString,
        '.5M .5M .5M .5M .5W .5M .5C .5M .5M .5M .5M .5W .5M .5C .5M .5M .5M .5M .5W .5M .5C .5M .5M .5M .5M' as MaterialPatternString,
        '.5M .5M .5M .5W .5M .5M .5M .5C .5M .5M .5W .5M .5M .5M .5C .5M .5M .5W .5M .5M .5M .5C .5M .5M .5M' as MaterialPatternString,
      ],
    }
  ),
  fade: new Strips('4e3fddc7-9db1-12bf-8341-6adca335dc50' as BoardID, 'Fade', {
    numRows: 16,
    rowHeight: 1,
    thickness: 1.5,
    pattern:
      '1W 0.5M 0.5W 1.5M 0.5W 1.5M 1.5M 1.5M 0.5W 1M 1W 0.5M' as MaterialPatternString,
    flip: true,
  }),
  mr_bobby: new Strips(
    'bfc45ac6-3315-76e6-afa2-4a31d19c6200' as BoardID,
    'Mr. Bobby',
    {
      numRows: 16,
      rowHeight: 1,
      thickness: 1.5,
      pattern:
        '1.8M .6W 1.2M .6W 1.2M .6W 1.2M .6W 1.2M .6W 1.2M .6W 1.2M' as MaterialPatternString,
      flip: true,
    }
  ),
  pinwheel: new Pinwheel(
    '8794545d-a9b6-42dc-ab88-d4d017e705b6' as BoardID,
    'Pinwheel',
    {
      numVertical: 8,
      numHorizontal: 6,
      pattern: '.75M .5W .25M .5W' as MaterialPatternString,
      thickness: 1.5,
    }
  ),
  plaid: new Plaid('89d8cd0c-79a7-44d1-8a92-8058e58da2aa' as BoardID, 'Plaid', {
    background: Material.M,
    wood1: Material.C,
    wood2: Material.W,
    startOn: 'background',
    colWidth: 1,
    rowHeight: 1,
    numCols: 11,
    numRows: 17,
    hStripe: '.25w' as MaterialPatternString,
    vStripe: '.25w' as MaterialPatternString,
    isHStripeOn1: true,
    isHStripeOn2: false,
    isVStripeOn1: true,
    isVStripeOn2: false,
  }),
  shelby: new Strips(
    '7af93624-9a16-315b-a9d6-d63b72075fdf' as BoardID,
    'Shelby',
    {
      numRows: 16,
      rowHeight: 1,
      thickness: 1.5,
      pattern: '1.3M 2.6M 2W .3M 2W 1.3M 2.6M' as MaterialPatternString,
      flip: true,
    }
  ),
  weave: new WeaveClass(
    'f417b40a-970b-1cc9-951f-254b46a32ca9' as BoardID,
    'Weave',
    {
      pattern: '.25W 1M .25W' as MaterialPatternString,
      horizontalSize: 3,
      verticalSize: 5,
      thickness: 1.5,
      background: '1.5C' as MaterialPatternString,
    }
  ),
  zig_zag: new ZigZag(
    'a50a099d-b968-70a0-ae61-fcff6eeeef3a' as BoardID,
    'Zig Zag',
    {
      numVertical: 8,
      numHorizontal: 6,
      pattern: '1M .25W .25M .5W' as MaterialPatternString,
      thickness: 1.5,
    }
  ),
};

export const POPULAR_BOARDS: Array<BoardBase> = [
  POPULAR_BOARDS_BY_NAME.checkerboard,
  POPULAR_BOARDS_BY_NAME.brick,
  POPULAR_BOARDS_BY_NAME.bubble,
  POPULAR_BOARDS_BY_NAME.chevron,
  POPULAR_BOARDS_BY_NAME.dna,
  POPULAR_BOARDS_BY_NAME.fade,
  POPULAR_BOARDS_BY_NAME.mr_bobby,
  POPULAR_BOARDS_BY_NAME.pinwheel,
  POPULAR_BOARDS_BY_NAME.plaid,
  POPULAR_BOARDS_BY_NAME.shelby,
  POPULAR_BOARDS_BY_NAME.weave,
  POPULAR_BOARDS_BY_NAME.zig_zag,
];

export const POPULAR_BOARDS_IDS: Array<BoardID> = POPULAR_BOARDS.map(
  ({ id }) => id
);

export const POPULAR_BOARDS_IDS_SET: Set<BoardID> = new Set(POPULAR_BOARDS_IDS);

export const DEFAULT_BOARDS_BY_TYPE: Record<BoardType, BoardBase> = {
  [BoardType.Chevron]: POPULAR_BOARDS_BY_NAME.chevron,
  [BoardType.Pinwheel]: POPULAR_BOARDS_BY_NAME.pinwheel,
  [BoardType.Plaid]: POPULAR_BOARDS_BY_NAME.plaid,
  [BoardType.QuarterSquare]: POPULAR_BOARDS_BY_NAME.bubble,
  [BoardType.Strips]: POPULAR_BOARDS_BY_NAME.checkerboard,
  [BoardType.StripsAdvanced]: POPULAR_BOARDS_BY_NAME.dna,
  [BoardType.Weave]: POPULAR_BOARDS_BY_NAME.weave,
  [BoardType.ZigZag]: POPULAR_BOARDS_BY_NAME.zig_zag,
};
